import React from 'react';
import { PageHeading, Heading, Text } from '../components/ui';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
    return (
        <div className="min-h-screen bg-neutral-50 px-0">
            <div className="max-w-4xl mx-auto py-8 px-4">
                <PageHeading>Terms of Service</PageHeading>
                
                <div className="bg-neutral-0 hairline minimal-padding mt-8 space-y-6">
                    <Text size="sm" className="text-neutral-900 opacity-60">
                        Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </Text>

                    <div>
                        <Text size="sm" className="text-neutral-900">
                            Welcome to 4RKED! These Terms of Service ("Terms") govern your access to and use of the 4RKED website, mobile applications, and related services (collectively, the "Service").
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>1. Eligibility & Accounts</Heading>
                        
                        <div className="mt-4 space-y-3">
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">1.1 Eligibility</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    You must be at least 13 years old (or the minimum age of digital consent in your jurisdiction) to use the Service. By using 4RKED, you represent that you meet this requirement.
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">1.2 Account Responsibility</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    You are responsible for:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Maintaining the confidentiality of your login credentials</li>
                                    <li>All activity that occurs under your account</li>
                                </ul>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    Please notify us immediately if you suspect any unauthorized use of your account.
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>2. The Service</Heading>
                        
                        <div className="mt-4 space-y-3">
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">2.1 Description</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    4RKED helps you save, organize, and share recipes, including by manually creating recipes and using tools like "Smart Transfer" to import recipes from other sources.
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">2.2 Smart Transfer</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    Smart Transfer is provided for your personal convenience. Importing a recipe from another website does not transfer any ownership or intellectual property rights in that recipe to you or to 4RKED. Original authors and websites retain their rights in their content.
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>3. User Content</Heading>
                        
                        <div className="mt-4 space-y-3">
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.1 Definition</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    "User Content" means any recipes, text, photos, notes, ratings, comments, or other material that you create, upload, import, or otherwise make available through the Service.
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.2 Ownership</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    You retain any rights you already hold in your User Content. 4RKED does not claim ownership over content you create or import.
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.3 License to 4RKED</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    By submitting User Content to the Service, you grant 4RKED a worldwide, non-exclusive, royalty-free, transferable license to host, store, display, reproduce, and use your User Content solely:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>To operate, maintain, and improve the Service</li>
                                    <li>To display content to you and others as you choose (e.g., when you share a recipe)</li>
                                    <li>To provide features such as search, recommendations, backup, and synchronization across devices</li>
                                </ul>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    This license ends when your User Content is deleted from our systems, subject to reasonable backup retention.
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">3.4 Your Responsibility for User Content</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    You are solely responsible for your User Content and for ensuring that:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>You have the necessary rights and permissions to use and share it</li>
                                    <li>Your content does not violate these Terms or applicable law</li>
                                </ul>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    4RKED is not responsible for User Content provided by you or other users.
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>4. Imported Recipes & Copyright</Heading>
                        
                        <div className="mt-4 space-y-3">
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">4.1 Imported Content is for Personal Use</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    When you import a recipe from a third-party source (e.g., website, blog, social media), you acknowledge and agree that:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Smart Transfer is intended primarily for personal, non-commercial use</li>
                                    <li>The original recipe author and/or website retains all rights in their content</li>
                                    <li>Importing a recipe does not grant you the right to publicly republish that content without permission</li>
                                </ul>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">4.2 Public Sharing of Imported Recipes</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    Publicly sharing or publishing imported recipes that reproduce the original author's creative expression (such as instructions, photos, and storytelling) may infringe their rights.
                                </Text>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    4RKED may:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Restrict public sharing of imported recipes to ingredients and a link to the original source, and/or</li>
                                    <li>Require that you create your own original version before sharing publicly</li>
                                </ul>
                                <Text size="sm" className="text-neutral-900 mt-2">
                                    You agree not to use the Service to publicly share imported content in a way that violates the rights of others.
                                </Text>
                            </div>
                            
                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium">4.3 Attribution</Text>
                                <Text size="sm" className="text-neutral-900 mt-1">
                                    When importing a recipe, you agree to:
                                </Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                                    <li>Provide and maintain accurate source information (URL, author/site name) where available</li>
                                    <li>Not remove or falsify attribution for third-party content</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>5. Prohibited Conduct</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            You agree that you will not:
                        </Text>
                        <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-2">
                            <li>Upload, import, or share content that you do not have the right to use or that infringes any third party's copyright, trademark, or other rights</li>
                            <li>Use the Service to copy, distribute, or publicly display recipes or other content from third-party sites in violation of those sites' terms or the rights of their owners</li>
                            <li>Attempt to circumvent any restrictions we implement to protect copyright or privacy</li>
                            <li>Use the Service for any illegal or unauthorized purpose</li>
                            <li>Interfere with or disrupt the Service, servers, or networks</li>
                            <li>Attempt to gain unauthorized access to any accounts, systems, or data</li>
                        </ul>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            We may remove or restrict access to any User Content that we believe violates these Terms or applicable law.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>6. DMCA & Copyright Complaints</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            We respect intellectual property rights and expect our users to do the same. If you believe that content on 4RKED infringes your copyright, please follow the steps described in our{' '}
                            <Link to="/dmca-policy" className="underline">DMCA Policy</Link>.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            We may remove or disable access to allegedly infringing content and may terminate the accounts of repeat infringers.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>7. Privacy</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            Your use of the Service is also governed by our{' '}
                            <Link to="/privacy-policy" className="underline">Privacy Policy</Link>, which explains how we collect, use, and protect your information. By using 4RKED, you agree to the practices described in the Privacy Policy.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>8. Disclaimers</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            The Service and all content are provided on an "as is" and "as available" basis, without warranties of any kind, whether express or implied, including any warranties of merchantability, fitness for a particular purpose, non-infringement, or course of dealing.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            4RKED does not guarantee:
                        </Text>
                        <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                            <li>The accuracy, completeness, or reliability of any recipes or other content on the Service</li>
                            <li>That any recipe will produce a desired result or be suitable for your dietary needs or allergies</li>
                        </ul>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            You use the Service at your own risk, including when preparing food based on recipes.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>9. Limitation of Liability</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            To the maximum extent permitted by law, 4RKED and its owners, employees, and affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or data, arising out of or in connection with your use of the Service.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            Our total liability for any claim arising out of or relating to the Service will not exceed the greater of:
                        </Text>
                        <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                            <li>The amount you paid to us (if any) in the 12 months before the event giving rise to the claim; or</li>
                            <li>One hundred U.S. dollars (US $100).</li>
                        </ul>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            Some jurisdictions do not allow certain limitations of liability, so some of the above may not apply to you.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>10. Indemnification</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            You agree to indemnify, defend, and hold harmless 4RKED and its owners, officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to:
                        </Text>
                        <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc mt-1">
                            <li>Your use of the Service</li>
                            <li>Your User Content</li>
                            <li>Your violation of these Terms or any applicable law</li>
                            <li>Your violation of any third party's rights</li>
                        </ul>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>11. Changes to the Service and Terms</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            We may modify or discontinue the Service (or any part of it) at any time. We may also update these Terms from time to time. When we do, we will revise the "Last updated" date above and may provide additional notice as required by law.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            Your continued use of the Service after any changes constitutes your acceptance of the updated Terms.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>12. Governing Law & Dispute Resolution</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            These Terms are governed by the laws of the State of Colorado, United States, without regard to its conflict-of-law principles.
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-2">
                            Any disputes arising out of or relating to these Terms or the Service will be resolved in the state or federal courts located in Colorado, and you consent to the jurisdiction of those courts.
                        </Text>
                    </div>

                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>13. Contact</Heading>
                        
                        <Text size="sm" className="text-neutral-900 mt-4">
                            If you have questions about these Terms, please contact us at:
                        </Text>
                        <Text size="sm" className="text-neutral-900 mt-1">
                            Email: support@4rked.com
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsOfService;

